import React from 'react';
import { motion } from 'framer-motion';
import { WeeklyGoals } from '@/types/career-sim';
import { Target, TrendingUp } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

interface WeekGoalsProps {
  weeklyGoals: WeeklyGoals;
  onContinue: () => void;
}

const WeekGoals: React.FC<WeekGoalsProps> = ({ weeklyGoals, onContinue }) => {
  const getPhaseEmoji = (phase: string) => {
    switch (phase) {
      case 'bedroom': return '🏠';
      case 'underground': return '🎤';
      case 'rising': return '⭐';
      case 'established': return '🏆';
      case 'mogul': return '👑';
      default: return '🎵';
    }
  };

  const getPhaseTitle = (phase: string) => {
    switch (phase) {
      case 'bedroom': return 'Bedroom Producer';
      case 'underground': return 'Underground Artist';
      case 'rising': return 'Rising Star';
      case 'established': return 'Established Act';
      case 'mogul': return 'Industry Mogul';
      default: return 'Artist';
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-gradient-to-br from-purple-900/90 to-blue-900/90 border-2 border-purple-500/50 rounded-2xl p-8 max-w-2xl w-full mx-4 shadow-2xl"
        initial={{ scale: 0.8, y: 50 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ type: 'spring', duration: 0.5 }}
      >
        <div className="text-center mb-6">
          <motion.div
            className="text-6xl mb-4"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 0.5 }}
          >
            {getPhaseEmoji(weeklyGoals.phase)}
          </motion.div>
          <h2 className="text-3xl font-bold text-white mb-2">Week {weeklyGoals.week}</h2>
          <p className="text-xl text-purple-200">{getPhaseTitle(weeklyGoals.phase)}</p>
        </div>

        <div className="bg-black/30 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Target className="h-5 w-5 text-yellow-400" />
            <h3 className="text-lg font-semibold text-white">Weekly Goals</h3>
          </div>

          <div className="space-y-4">
            {weeklyGoals.goals.fans && (
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-300">Fans</span>
                  <span className="text-white font-semibold">
                    {weeklyGoals.goals.fans.current} / {weeklyGoals.goals.fans.target}
                  </span>
                </div>
                <Progress 
                  value={(weeklyGoals.goals.fans.current / weeklyGoals.goals.fans.target) * 100} 
                  className="h-2"
                />
              </div>
            )}

            {weeklyGoals.goals.funds && (
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-300">Funds</span>
                  <span className="text-white font-semibold">
                    ${weeklyGoals.goals.funds.current} / ${weeklyGoals.goals.funds.target}
                  </span>
                </div>
                <Progress 
                  value={(weeklyGoals.goals.funds.current / weeklyGoals.goals.funds.target) * 100} 
                  className="h-2"
                />
              </div>
            )}

            {weeklyGoals.goals.releases && (
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-300">Releases</span>
                  <span className="text-white font-semibold">
                    {weeklyGoals.goals.releases.current} / {weeklyGoals.goals.releases.target}
                  </span>
                </div>
                <Progress 
                  value={(weeklyGoals.goals.releases.current / weeklyGoals.goals.releases.target) * 100} 
                  className="h-2"
                />
              </div>
            )}

            {weeklyGoals.goals.reputation && (
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-300">Reputation</span>
                  <span className="text-white font-semibold">
                    {weeklyGoals.goals.reputation.current} / {weeklyGoals.goals.reputation.target}
                  </span>
                </div>
                <Progress 
                  value={(weeklyGoals.goals.reputation.current / weeklyGoals.goals.reputation.target) * 100} 
                  className="h-2"
                />
              </div>
            )}
          </div>
        </div>

        {weeklyGoals.reward && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-4 w-4 text-yellow-400" />
              <h4 className="text-sm font-semibold text-yellow-300">Rewards for Completion</h4>
            </div>
            <div className="flex flex-wrap gap-3 text-sm text-yellow-100">
              {weeklyGoals.reward.funds && <span>💰 +${weeklyGoals.reward.funds}</span>}
              {weeklyGoals.reward.reputation && <span>⭐ +{weeklyGoals.reward.reputation} Rep</span>}
              {weeklyGoals.reward.unlocks && weeklyGoals.reward.unlocks.length > 0 && (
                <span>🎁 New Actions Unlocked</span>
              )}
            </div>
          </div>
        )}

        <motion.button
          onClick={onContinue}
          className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold py-4 rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all shadow-lg"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Let's Go! 🚀
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

export default WeekGoals;
